import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface AppState {
  isLoading: boolean
  sidebarCollapsed: boolean
  theme: 'light' | 'dark' | 'system'
}

export const useAppStore = defineStore('app', () => {
  // State
  const isLoading = ref(false)
  const sidebarCollapsed = ref(false)
  const theme = ref<'light' | 'dark' | 'system'>('dark')
  const updateAvailable = ref(false)
  const onlineStatus = ref(true)

  // Getters
  const isDarkMode = computed(() => {
    if (theme.value === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return theme.value === 'dark'
  })

  // Actions
  const setLoading = (loading: boolean) => {
    isLoading.value = loading
  }

  const toggleSidebar = () => {
    sidebarCollapsed.value = !sidebarCollapsed.value
    localStorage.setItem('sidebar_collapsed', String(sidebarCollapsed.value))
  }

  const setTheme = (newTheme: 'light' | 'dark' | 'system') => {
    theme.value = newTheme
    localStorage.setItem('theme', newTheme)
    updateThemeClass()
  }

  const toggleDarkMode = () => {
    const newTheme = theme.value === 'dark' ? 'light' : 'dark'
    setTheme(newTheme)
  }


  const updateThemeClass = () => {
    const html = document.documentElement
    
    if (isDarkMode.value) {
      html.classList.add('dark')
    } else {
      html.classList.remove('dark')
    }
  }

  const initializeTheme = () => {
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | 'system'
    if (savedTheme) {
      theme.value = savedTheme
    } else {
      // Default to dark experience for first-time visitors
      theme.value = 'dark'
      localStorage.setItem('theme', 'dark')
    }

    // Load sidebar state
    const savedSidebarState = localStorage.getItem('sidebar_collapsed')
    if (savedSidebarState) {
      sidebarCollapsed.value = savedSidebarState === 'true'
    }


    // Apply theme
    updateThemeClass()

    // Listen for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (theme.value === 'system') {
        updateThemeClass()
      }
    })
  }

  // Additional actions
  const setUpdateAvailable = (available: boolean) => {
    updateAvailable.value = available
  }
  
  const setOnlineStatus = (online: boolean) => {
    onlineStatus.value = online
  }

  return {
    // State
    isLoading,
    sidebarCollapsed,
    theme,
    updateAvailable,
    onlineStatus,
    
    // Getters
    isDarkMode,
    
    // Actions
    setLoading,
    toggleSidebar,
    setTheme,
    toggleDarkMode,
    setUpdateAvailable,
    setOnlineStatus,
    initializeTheme
  }
}, {
  persist: {
    key: 'sugarm-app',
    storage: localStorage,
    paths: ['theme', 'sidebarCollapsed']
  }
})
